import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ContextualMenuType, Sections } from 'src/app/settings/constants';
import { CompaniesService } from 'src/app/settings/services/companies.service';
import { ContextualMenuService } from 'src/app/settings/services/contextual-menu.service';
import { countryPrefixesAndMasksByIso, getPhoneMask } from 'src/app/shared/utils/phone-mask.util';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  updateValue,
  updateLocation,
  validateEmail,
  validateUserCode,
  validateWebUrl,
  validatorString,
  checkCoords,
  checkEmailFormat,
} from 'src/app/settings/validators';
import { MatDialog } from '@angular/material/dialog';
import { ReceiversService } from 'src/app/shared/services/receivers.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ReceiverServer } from 'src/app/shared/models/receiver-model';

const MAX_EZVIZ_ACCOUNT_NAME_LENGTH = 25;

@Component({
  selector: 'app-company-edit-info',
  templateUrl: './company-edit-info.component.html',
  styleUrls: ['./company-edit-info.component.scss'],
})
export class CompanyEditInfoComponent implements OnInit, AfterViewInit {
  editInfoForm: FormGroup;
  submited: boolean;

  contextualMenuType = ContextualMenuType;
  location: string = '';
  notificationEmail: string = '';
  expand: boolean = false;

  contextualMenuWidth = '290px';

  textControl = {
    name: /[^a-zA-Z0-9 ]/g,
    receiverName: /[^a-zA-Z0-9 ]/g,
    linkName: /[^a-zA-Z0-9.]/g,
    apiKey: /[^a-zA-Z0-9]/g,
  };

  mask: string = '(+00) 0000000000000';

  invalidParams = {};

  countrySelected: string = '';
  countries: string[] = [];

  languages: string[] = ['es', 'en', 'pt'];
  serverLocation: ReceiverServer | null = null;

  addReceiverEvent: number = 0;
  addUserEvent: number = 0;

  /** Valor original do nome da conta Ezviz ao carregar (edição). Se não mudar, o form não valida. */
  private originalEzvizAccountName: string | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    public companiesService: CompaniesService,
    public contextualMenuService: ContextualMenuService,
    public receiversService: ReceiversService,
    public dialog: MatDialog,
    public authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeCompany();
    this.resetFields();
    this.countries = Object.keys(countryPrefixesAndMasksByIso);
  }

  hasPermissionRole(): boolean {
    return this.authService.hasRoleAdmin() || this.authService.hasRoleDistributor() || this.authService.hasAdminCompany();
  }
  
  isAdmin(): boolean {
    return this.authService.hasRoleAdmin();
  }

  isEdit(): boolean {
    return this.hasPermissionRole() && this.companiesService.currentSection === Sections.Edit;
  }

  get isEzvizAccountDisabled() {
    const res = this.companiesService.isBeingCreated.getValue();
    return res ? 'ezviz-enabled' : 'ezviz-disabled';
  }

  get isIpDisabled() {
    const res = this.companiesService.isBeingCreated.getValue();
    return res ? 'ip-enabled' : 'ip-disabled';
  }

  parseCompanyName(companyName: string) {
    if (!companyName) return '';

    if (this.isEdit() && this.companiesService.currentCompany.ezvizAccountName)
      return this.companiesService.currentCompany.ezvizAccountName;

    const parsedName = companyName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, MAX_EZVIZ_ACCOUNT_NAME_LENGTH)
      .toLowerCase();

    return parsedName;
  }

  onSelectCountry(event: any) {
    if (event === 'globe') {
      this.companiesService.currentCompany.country = null;
      this.editInfoForm.get('country').setValue(null);
    }

    if (countryPrefixesAndMasksByIso[event]) {
      this.companiesService.currentCompany.country = { iso: event };
      this.editInfoForm.get('country').setValue({ iso: event });
    }
  }

  ngAfterViewInit(): void {
    this.userChangedTab();
  }

  onCompanyNameChange(event: any) {
    if (!this.isEdit()) {
      this.companiesService.currentCompany.ezvizAccountName = this.parseCompanyName(event);
    }
  }

  private initializeCompany(): void {
    if (this.companiesService.currentCompany.whatsappNumber)
      this.mask = getPhoneMask(this.companiesService.currentCompany.whatsappNumber);

    this.location =
      this.companiesService.currentCompany?.location?.lat && this.companiesService.currentCompany?.location?.lng
        ? `${this.companiesService.currentCompany?.location?.lat}, ${this.companiesService.currentCompany?.location?.lng}`
        : '';

    if (this.companiesService?.currentCompany?.country?.iso) {
      this.countrySelected = this.companiesService.currentCompany.country.iso;
    }

    this.notificationEmail = this.companiesService.currentCompany.notificationEmail?.join(',') ?? '';

    if (this.isEdit() && this.companiesService.currentCompany.ezvizAccountName) {
      this.originalEzvizAccountName = this.companiesService.currentCompany.ezvizAccountName;
    } else {
      this.originalEzvizAccountName = null;
    }
    this.companiesService.currentCompany.ezvizAccountName = this.parseCompanyName(
      this.companiesService.currentCompany.name
    );

    this.serverLocation = this.receiversService.receiverServers.find(
      (location) => location.ip === this.companiesService.currentCompany.ip
    );

    this.initializeForm();
  }

  private makeFieldTarnished(key) {
    this.editInfoForm.get(key).markAsTouched();
    this.editInfoForm.get(key).markAsDirty();
    this.editInfoForm.get(key).updateValueAndValidity();
  }

  private applyInterjectedErrors(key) {
    Object.keys(this.editInfoForm.get(key).errors).forEach((firstKey) => {
      const getError = this.getErrorType(key, firstKey);
      this.invalidParams[key] = getError;
    });
  }

  private interjectErrorForFieldName(key) {
    this.makeFieldTarnished(key);

    if (!this.companiesService.currentCompany[key]) {
      this.editInfoForm.get(key).setErrors({ required: true });
      this.applyInterjectedErrors(key);
    }
  }

  private interjectErrorForFieldEmail(key) {
    this.makeFieldTarnished(key);

    const value = this.companiesService.currentCompany[key];
    const isEmptyEmailInEdit = key === 'email' && this.isEdit() && !value;

    if (isEmptyEmailInEdit) {
      this.editInfoForm.get(key).setErrors(null);
      return;
    }
    if (value && !checkEmailFormat(value)) {
      this.editInfoForm.get(key).setErrors({ invalidParam: true });
      this.applyInterjectedErrors(key);
    } else if (!value) {
      this.editInfoForm.get(key).setErrors({ required: true });
      this.applyInterjectedErrors(key);
    }
  }

  private userChangedTab(): void {
    /**
     * if user changed tab and its edit mode
     * check for fields with problems and interject errors by force
     */
    if (this.companiesService.currentSection === Sections.Edit) {
      Object.keys(this.editInfoForm.controls).forEach((key) => {
        if (key === 'name') this.interjectErrorForFieldName(key);
        if (key === 'email') this.interjectErrorForFieldEmail(key);
      });
    }
  }

  private initializeForm(): void {
    const isEditMode = this.isEdit();
    this.editInfoForm = this.formBuilder.group({
      name: [
        this.companiesService.currentCompany.name,
        [
          Validators.required,
          (event) => validatorString('name', event, this.editInfoForm, this.companiesService.currentCompany),
        ],
      ],
      email: [
        this.companiesService.currentCompany.email,
        [
          ...(isEditMode ? [] : [Validators.required]),
          (event) => validateEmail('email', event, this.editInfoForm, this.companiesService.currentCompany),
        ],
      ],
      contact: [
        this.companiesService.currentCompany.contact,
        [(event) => validateEmail('contact', event, this.editInfoForm, this.companiesService.currentCompany)],
      ],
      notificationEmail: [this.notificationEmail, [(event) => this.validateNotifEmail('notificationEmail', event)]],
      techUserCode: [
        this.companiesService.currentCompany.techUserCode,
        [(event) => validateUserCode('techUserCode', event, this.editInfoForm, this.companiesService.currentCompany)],
      ],
      webUrl: [
        this.companiesService.currentCompany.webUrl,
        [(event) => validateWebUrl('webUrl', event, this.editInfoForm, this.companiesService.currentCompany)],
      ],
      linkName: [
        this.companiesService.currentCompany.linkName,
        [(event) => validatorString('linkName', event, this.editInfoForm, this.companiesService.currentCompany)],
      ],
      country: [
        this.companiesService.currentCompany.country,
        [Validators.required, (event) => this.onSelectCountry(event)],
      ],
      whatsappNumber: [
        this.companiesService.currentCompany.whatsappNumber,
        [(event) => this.applyMask('whatsappNumber', event)],
      ],
      location: [this.location, [(event) => this.validateLocation('location', event)]],
      ezvizAccountName: [
        this.companiesService.currentCompany.ezvizAccountName,
        [(event) => this.validateEzvizAccountName('ezvizAccountName', event)],
      ],
      ip: [
        this.companiesService.currentCompany.ip,
        [...(isEditMode ? [] : [Validators.required])],
      ],
    });
  }

  isAddReceiverButtonDisabled(): boolean {
    const hasReceiver =
      (this.companiesService.currentCompany?.receivers?.length ?? 0) > 0;
    return !this.isEdit() || !this.serverLocation?.ip || hasReceiver;
  }

  onPhoneChange(event: any) {
    this.companiesService.currentCompany.whatsappNumber = event;
    this.editInfoForm.get('whatsappNumber').setValue(event);
  }

  addReceiver() {
    this.addReceiverEvent++;
  }

  addUser() {
    this.addUserEvent++;
  }

  onContextalMenu(event, value, type, id) {
    const elemento1 = document.getElementById('language-selector');
    const width = elemento1.offsetWidth;
    this.contextualMenuWidth = `${width}px`;

    const target = document.getElementById(id);
    this.contextualMenuService.processContextualMenuPosition(event, target, undefined, type, width - 40);
    this.expand = value;
  }

  onCloseContextMenu() {
    this.expand = false;
  }

  setServer(ip: string) {
    const server = this.receiversService.receiverServers.find((s) => s.ip === ip);
    if (server) {
      this.serverLocation = server;
      this.companiesService.currentCompany.ip = server.ip;
      this.editInfoForm.get('ip').setValue(server.ip);
    }
  }

  setLanguage(value: string) {
    this.companiesService.currentCompany.language = value;
  }

  blockEKey(event: KeyboardEvent) {
    if (event.key.toLowerCase() === 'e') {
      event.preventDefault();
    }
  }

  getError(event) {
    const field = this.editInfoForm.get(event);
    return (this.submited || field?.touched) && (field?.errors?.required || field?.errors?.invalidParam);
  }

  getErrorType(event, type) {
    return this.editInfoForm.get(event)?.errors?.[type];
  }

  getWhatsappError() {
    return this.editInfoForm?.controls.whatsappNumber.status === 'INVALID';
  }

  onSubmit(callback = undefined) {
    this.submited = true;
    if (this.editInfoForm.valid && callback) {
      callback();
    }
  }

  resetFields() {
    this.companiesService.resetEditFields = () => {
      this.initializeCompany();
    };
  }

  // ----------------------- controls -------------------------------
  applyMask(key, event) {
    if (!event.value || this.companiesService.currentCompany[key] === event.value) return;
    updateValue(key, event.value, this.editInfoForm, this.companiesService.currentCompany);

    this.mask = getPhoneMask(event.value);
    return null;
  }

  validateNotifEmail(key, event) {
    if (this.notificationEmail === event.value) return;
    updateValue(key, event.value, this.editInfoForm, this.companiesService.currentCompany);

    let emails = this.notificationEmail.trim().split(',');
    let invalidParam = false;
    emails = emails.filter((email) => {
      const res = checkEmailFormat(email);

      if (!res && email != '') invalidParam = true;
      return res;
    });

    this.companiesService.currentCompany[key] = emails;
    return invalidParam ? { invalidParam } : null;
  }

  validateEzvizAccountName(key, event) {
    if (this.isEdit() && this.originalEzvizAccountName != null && event.value === this.originalEzvizAccountName) {
      return null;
    }
    const invalidParam = this.isEdit()
      ? event.value.replace(/\s/g, '').length < 4 || !/^[a-zA-Z0-9]+$/.test(event.value.replace(/^.{0,3}\s/, ''))
      : event.value.replace(/\s/g, '').length < 4 || !/^[a-z0-9]+$/.test(event.value.replace(/^.{0,3}\s/, ''));
    return invalidParam ? { invalidParam } : null;
  }

  validateLocation(key, event) {
    if (this.location === event.value) return;

    const coords = checkCoords(event.value);
    this.location = event.value.toString();
    if (coords.success) updateLocation(key, coords.location, this.editInfoForm, this.companiesService.currentCompany);

    const invalidParam = !coords.success;
    return invalidParam ? { invalidParam } : null;
  }
}
